import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Send, Loader2, X, Plus, CheckCircle2 } from "lucide-react";
import Header from "@/components/Header";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { categoryApi, cityApi, api } from "@/lib/api";
import { toast } from "sonner";

const empty = {
  title: "", description: "", category_key: "", city_key: "",
  venue: "", address: "", image_url: "", images: [], event_date: "", event_time: "",
  price: 0, is_free: false, booking_type: "redirect", booking_url: "",
  contact_number: "", tags: [],
  submitter_name: "", submitter_email: "", submitter_phone: "",
};

export default function ListEvent() {
  const [form, setForm] = useState(empty);
  const [cats, setCats] = useState([]);
  const [cities, setCities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [newImg, setNewImg] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    categoryApi.list({ enabled: true }).then(setCats);
    cityApi.list({ enabled: true }).then(setCities);
  }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const addImage = () => {
    const u = newImg.trim();
    if (!u) return;
    set("images", [...(form.images || []), u]);
    if (!form.image_url) set("image_url", u);
    setNewImg("");
  };
  const removeImage = (i) =>
    set("images", form.images.filter((_, idx) => idx !== i));

  const addTag = () => {
    const t = newTag.trim();
    if (!t) return;
    set("tags", [...new Set([...(form.tags || []), t])]);
    setNewTag("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.event_date || !form.category_key || !form.city_key || !form.submitter_email || !form.submitter_name) {
      toast.error("Please fill required fields");
      return;
    }
    if (!form.image_url && form.images.length === 0) {
      toast.error("Add at least one image URL");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        image_url: form.image_url || form.images[0],
      };
      await api.post("/events/submit", payload);
      setDone(true);
      toast.success("Submitted! Admin will review and publish it.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setSaving(false); }
  };

  if (done) {
    return (
      <div className="grain relative min-h-screen">
        <Header />
        <main className="pt-32 pb-24 max-w-2xl mx-auto px-6 text-center">
          <div className="mx-auto h-20 w-20 rounded-full bg-emerald-100 grid place-items-center mb-6">
            <CheckCircle2 className="h-10 w-10 text-emerald-600" />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">
            Thanks — your event is in review!
          </h1>
          <p className="text-gray-600 mt-4 text-lg">
            Our team will review your submission and publish it shortly. You'll hear back at{" "}
            <span className="font-bold text-[#FA003F]">{form.submitter_email}</span>.
          </p>
          <div className="mt-8 flex gap-3 justify-center">
            <Button onClick={() => nav("/")} className="rounded-full h-11 px-6 bg-[#1A1A1A] hover:bg-[#FA003F]">
              Back to home
            </Button>
            <Button
              variant="outline"
              onClick={() => { setForm(empty); setDone(false); }}
              className="rounded-full h-11 px-6"
            >
              Submit another
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="grain relative min-h-screen">
      <Header />
      <main className="pt-24 md:pt-28 pb-24">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs font-bold tracking-widest uppercase text-[#FA003F] mb-6">
            <Sparkles className="h-3.5 w-3.5" /> Free listing
          </div>
          <h1 className="font-display text-5xl sm:text-6xl font-black leading-[0.95] tracking-tighter">
            List your event <span className="text-[#FA003F]">on Fanpit.</span>
          </h1>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl">
            Tell us the essentials. Your event goes to our editors for a quick review and then goes live in front of thousands of curious locals.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-6" data-testid="list-event-form">
            <Card>
              <SectionTitle>Event essentials</SectionTitle>
              <div className="space-y-4">
                <Field label="Event title *">
                  <Input data-testid="submit-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="h-12 rounded-xl" />
                </Field>
                <Field label="About the event">
                  <Textarea data-testid="submit-description" value={form.description} onChange={(e) => set("description", e.target.value)} className="rounded-xl min-h-32" />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Category *">
                    <Select value={form.category_key} onValueChange={(v) => set("category_key", v)}>
                      <SelectTrigger className="h-12 rounded-xl" data-testid="submit-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>{cats.map((c) => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="City *">
                    <Select value={form.city_key} onValueChange={(v) => set("city_key", v)}>
                      <SelectTrigger className="h-12 rounded-xl" data-testid="submit-city-select"><SelectValue placeholder="Select city" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {cities.map((c) => <SelectItem key={c.key} value={c.key}>{c.name}, {c.state}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Venue"><Input value={form.venue} onChange={(e) => set("venue", e.target.value)} className="h-12 rounded-xl" /></Field>
                  <Field label="Address"><Input value={form.address} onChange={(e) => set("address", e.target.value)} className="h-12 rounded-xl" /></Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Event date *">
                    <Input type="date" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} className="h-12 rounded-xl" data-testid="submit-date" />
                  </Field>
                  <Field label="Time">
                    <Input value={form.event_time} onChange={(e) => set("event_time", e.target.value)} placeholder="8:00 PM" className="h-12 rounded-xl" />
                  </Field>
                </div>
              </div>
            </Card>

            <Card>
              <SectionTitle>Images</SectionTitle>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    value={newImg}
                    onChange={(e) => setNewImg(e.target.value)}
                    placeholder="Paste an image URL (poster, cover, gallery photo)"
                    className="h-12 rounded-xl"
                    data-testid="submit-image-url"
                  />
                  <Button type="button" onClick={addImage} className="h-12 rounded-full bg-[#FA003F] hover:bg-[#C40031] px-5">
                    <Plus className="h-4 w-4 mr-1" /> Add
                  </Button>
                </div>
                {form.images.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {form.images.map((src, i) => (
                      <div key={i} className="relative group rounded-lg overflow-hidden aspect-square border border-black/10">
                        <img src={src} alt="" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="h-3 w-3" />
                        </button>
                        {i === 0 && (
                          <span className="absolute bottom-1 left-1 text-[10px] font-bold bg-[#FA003F] text-white px-1.5 py-0.5 rounded">Cover</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>

            <Card>
              <SectionTitle>Ticketing & booking</SectionTitle>
              <div className="space-y-4">
                <ToggleRow label="This is a free event" checked={form.is_free} onChange={(v) => { set("is_free", v); if (v) set("price", 0); }} />
                {!form.is_free && (
                  <Field label="Price (₹)">
                    <Input type="number" min={0} value={form.price} onChange={(e) => set("price", e.target.value)} className="h-12 rounded-xl" />
                  </Field>
                )}
                <Field label="How should users book?">
                  <div className="grid grid-cols-2 gap-2">
                    {["redirect", "contact"].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => set("booking_type", v)}
                        className={`h-12 rounded-full text-sm font-bold border transition-colors capitalize ${
                          form.booking_type === v ? "bg-[#FA003F] text-white border-transparent" : "bg-white text-[#1A1A1A] border-black/10 hover:border-[#FA003F]/40"
                        }`}
                      >
                        {v === "redirect" ? "External booking URL" : "Contact number"}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Booking URL"><Input value={form.booking_url} onChange={(e) => set("booking_url", e.target.value)} placeholder="https://…" className="h-12 rounded-xl" /></Field>
                <Field label="Contact number"><Input value={form.contact_number} onChange={(e) => set("contact_number", e.target.value)} placeholder="+91 …" className="h-12 rounded-xl" /></Field>

                <Field label="Tags">
                  <div className="flex gap-2">
                    <Input value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="e.g. Live Music" className="h-10 rounded-full" />
                    <Button type="button" onClick={addTag} className="rounded-full h-10 bg-[#1A1A1A]">Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {form.tags?.map((t) => (
                      <span key={t} className="inline-flex items-center gap-1 rounded-full bg-[#FA003F]/10 text-[#FA003F] text-xs font-bold px-3 py-1">
                        {t}
                        <button type="button" onClick={() => set("tags", form.tags.filter((x) => x !== t))} className="hover:text-red-600"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                </Field>
              </div>
            </Card>

            <Card>
              <SectionTitle>About you (organizer)</SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Your name *">
                  <Input data-testid="submit-name" value={form.submitter_name} onChange={(e) => set("submitter_name", e.target.value)} className="h-12 rounded-xl" />
                </Field>
                <Field label="Email *">
                  <Input data-testid="submit-email" type="email" value={form.submitter_email} onChange={(e) => set("submitter_email", e.target.value)} className="h-12 rounded-xl" />
                </Field>
                <Field label="Phone">
                  <Input data-testid="submit-phone" value={form.submitter_phone} onChange={(e) => set("submitter_phone", e.target.value)} className="h-12 rounded-xl" />
                </Field>
              </div>
            </Card>

            <div className="flex items-center justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => nav("/")} className="h-12 rounded-full px-6">Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="submit-event-btn" className="h-12 rounded-full bg-[#FA003F] hover:bg-[#C40031] px-8 font-bold text-base">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Submit for review</>}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

function Card({ children }) {
  return <div className="bg-white rounded-3xl border border-black/10 p-6 md:p-8">{children}</div>;
}
function SectionTitle({ children }) {
  return <h3 className="font-display font-black text-xl mb-4">{children}</h3>;
}
function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-gray-500">{label}</Label>{children}</div>;
}
function ToggleRow({ label, checked, onChange }) {
  return (
    <div className="flex items-center justify-between border border-black/5 rounded-xl px-4 py-3">
      <Label className="text-sm font-medium">{label}</Label>
      <Switch checked={checked} onCheckedChange={onChange} className="data-[state=checked]:bg-[#FA003F]" />
    </div>
  );
}
